'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';


import { verifyLicenseAction } from '@/app/actions/licenseActions';
import { formatDate } from '@/lib/licenseUtils';
import type { VerificationResponse } from '@/lib/licenseUtils';
import {
  Search,
  Shield,
  Copy,
  Printer,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import Icon from '@/components/ui/AppIcon';

type UIState = 'idle' | 'loading' | 'result';

const RESULT_CONFIG = {
  VALID: {
    title: 'VALID',
    message: 'This software license is valid and active.',
    icon: CheckCircle2,
    headerBg: 'bg-green-600',
    borderColor: 'border-green-200',
    badgeBg: 'bg-green-50 text-green-700 border-green-200',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    bannerBg: 'bg-green-50',
    bannerBorder: 'border-green-200',
    bannerText: 'text-green-800',
  },
  EXPIRED: {
    title: 'EXPIRED',
    message: 'This software license has expired. Please contact the administrator or proceed with license renewal/purchase.',
    icon: Clock,
    headerBg: 'bg-amber-500',
    borderColor: 'border-amber-200',
    badgeBg: 'bg-amber-50 text-amber-700 border-amber-200',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    bannerBg: 'bg-amber-50',
    bannerBorder: 'border-amber-200',
    bannerText: 'text-amber-800',
  },
  NOT_ACTIVE: {
    title: 'NOT ACTIVE',
    message: 'This software license is no longer active. Please check with the administrator.',
    icon: XCircle,
    headerBg: 'bg-red-600',
    borderColor: 'border-red-200',
    badgeBg: 'bg-red-50 text-red-700 border-red-200',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    bannerBg: 'bg-red-50',
    bannerBorder: 'border-red-200',
    bannerText: 'text-red-800',
  },
  NOT_FOUND: {
    title: 'NOT FOUND',
    message: 'Software license does not exist. Please check with the administrator or purchase a valid license.',
    icon: AlertTriangle,
    headerBg: 'bg-slate-500',
    borderColor: 'border-slate-200',
    badgeBg: 'bg-slate-100 text-slate-600 border-slate-200',
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-500',
    bannerBg: 'bg-slate-50',
    bannerBorder: 'border-slate-200',
    bannerText: 'text-slate-700',
  },
};

export default function PublicCheckerClient() {
  const [inputValue, setInputValue] = useState('');
  const [uiState, setUiState] = useState<UIState>('idle');
  const [verificationData, setVerificationData] = useState<VerificationResponse | null>(null);
  const [inputError, setInputError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);
  const searchParams = useSearchParams();

  // Auto-verify when ?license= query param is present
  useEffect(() => {
    const licenseParam = searchParams.get('license');
    if (licenseParam && licenseParam.trim()) {
      const decoded = decodeURIComponent(licenseParam.trim());
      setInputValue(decoded);
      setUiState('loading');
      verifyLicenseAction(decoded)
        .then((result) => {
          setVerificationData(result);
          setUiState('result');
          setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        })
        .catch((err) => {
          console.error('Verification failed:', err);
          setUiState('idle');
          setInputError('Verification service unavailable. Please try again.');
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheck = async () => {
    setInputError('');
    if (!inputValue.trim()) {
      setInputError('Please enter a Software License Number');
      return;
    }
    setUiState('loading');
    try {
      const result = await verifyLicenseAction(inputValue);
      setVerificationData(result);
      setUiState('result');
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err) {
      console.error('Verification failed:', err);
      setUiState('idle');
      setInputError('Verification service unavailable. Please try again.');
    }
  };

  const handleReset = () => {
    setInputValue('');
    setVerificationData(null);
    setUiState('idle');
    setInputError('');
    setShowQR(false);
  };

  const handleCopy = () => {
    if (!verificationData?.license) return;
    const text = [
      `Verification Reference: ${verificationData.verificationReference}`,
      `License Number: ${verificationData.license.software_license_number}`,
      `License Name: ${verificationData.license.license_name}`,
      `Status: ${verificationData.result}`,
      `Customer: ${verificationData.license.customer_name}`,
      `Project: ${verificationData.license.project_name}`,
      `Valid From: ${formatDate(verificationData.license.valid_from)}`,
      `Valid Until: ${formatDate(verificationData.license.valid_until)}`,
      `Verified At: ${new Date(verificationData.verificationTimestamp).toLocaleString('en-GB')}`,
    ].join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const lic = verificationData?.license;
    const pageW = 210;
    const pageH = 297;
    const margin = 16;
    const contentW = pageW - margin * 2;

    // ── Colour palette ──────────────────────────────────────────────
    const NAVY      = [15, 40, 90]    as [number, number, number]; // deep navy
    const NAVY_MID  = [30, 64, 140]   as [number, number, number]; // mid navy accent
    const NAVY_LIGHT= [235, 240, 255] as [number, number, number]; // very light navy tint
    const GREEN     = [22, 163, 74]   as [number, number, number];
    const GREEN_L   = [240, 253, 244] as [number, number, number];
    const GREEN_MID = [34, 197, 94]   as [number, number, number];
    const AMBER     = [180, 100, 0]   as [number, number, number];
    const AMBER_L   = [255, 251, 235] as [number, number, number];
    const RED       = [185, 28, 28]   as [number, number, number];
    const RED_L     = [254, 242, 242] as [number, number, number];
    const SLATE     = [100, 116, 139] as [number, number, number];
    const SLATE_L   = [248, 250, 252] as [number, number, number];
    const WHITE     = [255, 255, 255] as [number, number, number];
    const DARK      = [15, 23, 42]    as [number, number, number];
    const BORDER    = [220, 228, 240] as [number, number, number];
    const LABEL_CLR = [120, 135, 160] as [number, number, number];

    const statusResult = verificationData?.result ?? 'NOT_FOUND';
    let statusColor: [number,number,number];
    let statusBg: [number,number,number];
    let statusBorder: [number,number,number];
    let statusLabel: string;
    if (statusResult === 'VALID')           { statusColor = GREEN; statusBg = GREEN_L; statusBorder = GREEN_MID; statusLabel = 'VALID'; }
    else if (statusResult === 'EXPIRED')    { statusColor = AMBER; statusBg = AMBER_L; statusBorder = AMBER; statusLabel = 'EXPIRED'; }
    else if (statusResult === 'NOT_ACTIVE') { statusColor = RED;   statusBg = RED_L;   statusBorder = RED;   statusLabel = 'NOT ACTIVE'; }
    else                                    { statusColor = SLATE; statusBg = SLATE_L; statusBorder = SLATE; statusLabel = 'NOT FOUND'; }

    // ── Helper: rounded rect ─────────────────────────────────────────
    const roundedRect = (x: number, y: number, w: number, h: number, r: number, fill?: [number,number,number], stroke?: [number,number,number], lw = 0.25) => {
      if (fill)   { doc.setFillColor(...fill); }
      if (stroke) { doc.setDrawColor(...stroke); doc.setLineWidth(lw); } else { doc.setDrawColor(0,0,0,0); }
      doc.roundedRect(x, y, w, h, r, r, fill && stroke ? 'FD' : fill ? 'F' : 'D');
    };

    // ── Helper: label + value pair ───────────────────────────────────
    const labelValue = (label: string, value: string, x: number, y: number, colW = 85, valueColor?: [number,number,number]) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...LABEL_CLR);
      doc.text(label.toUpperCase(), x, y);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...(valueColor ?? DARK));
      const lines = doc.splitTextToSize(value || '—', colW);
      doc.text(lines[0] ?? '—', x, y + 4.5);
    };

    // ── Generate QR code data URL via hidden canvas ──────────────────
    let qrDataUrl: string | null = null;
    const verifyUrl = typeof window !== 'undefined'
      ? `${window.location.origin}/public-license-checker?license=${encodeURIComponent(verificationData?.license?.software_license_number ?? inputValue.trim())}`
      : `https://licenseche2524.builtwithrocket.new/public-license-checker?license=${encodeURIComponent(verificationData?.license?.software_license_number ?? inputValue.trim())}`;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 200;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Use qrcode library via dynamic import
        const QRCode = await import('qrcode').catch(() => null);
        if (QRCode) {
          await QRCode.default.toCanvas(canvas, verifyUrl, { width: 200, margin: 1, color: { dark: '#0f2860', light: '#ffffff' } });
          qrDataUrl = canvas.toDataURL('image/png');
        }
      }
    } catch (_) { /* fallback handled below */ }

    // Fallback: render QRCodeCanvas to a hidden DOM canvas
    if (!qrDataUrl) {
      try {
        const hiddenDiv = document.createElement('div');
        hiddenDiv.style.position = 'absolute';
        hiddenDiv.style.left = '-9999px';
        hiddenDiv.style.top = '-9999px';
        document.body.appendChild(hiddenDiv);
        const { createRoot } = await import('react-dom/client');
        const root = createRoot(hiddenDiv);
        await new Promise<void>((resolve) => {
          root.render(
            React.createElement(QRCodeCanvas, {
              value: verifyUrl,
              size: 200,
              id: 'pdf-qr-canvas',
              bgColor: '#ffffff',
              fgColor: '#0f2860',
            })
          );
          setTimeout(resolve, 300);
        });
        const qrCanvas = hiddenDiv.querySelector('canvas') as HTMLCanvasElement | null;
        if (qrCanvas) {
          qrDataUrl = qrCanvas.toDataURL('image/png');
        }
        root.unmount();
        document.body.removeChild(hiddenDiv);
      } catch (_) { /* ignore */ }
    }

    // ── Load logo as base64 ──────────────────────────────────────────
    let logoDataUrl: string | null = null;
    let logoNaturalW = 0;
    let logoNaturalH = 0;
    try {
      const response = await fetch('/assets/images/cxlogo-yJQWiUVo-1775639521249.png');
      const blob = await response.blob();
      logoDataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      // Get natural dimensions for aspect-ratio-preserving render
      if (logoDataUrl) {
        await new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => {
            logoNaturalW = img.naturalWidth;
            logoNaturalH = img.naturalHeight;
            resolve();
          };
          img.onerror = () => resolve();
          img.src = logoDataUrl as string;
        });
      }
    } catch (_) { /* fallback to text */ }

    // ════════════════════════════════════════════════════════════════
    // PAGE — clean white background
    // ════════════════════════════════════════════════════════════════
    doc.setFillColor(...WHITE);
    doc.rect(0, 0, pageW, pageH, 'F');

    // Outer border — thin navy
    doc.setDrawColor(...NAVY_MID);
    doc.setLineWidth(0.4);
    doc.rect(8, 8, pageW - 16, pageH - 16, 'D');

    // Inner thin line
    doc.setDrawColor(...NAVY_LIGHT);
    doc.setLineWidth(0.2);
    doc.rect(10, 10, pageW - 20, pageH - 20, 'D');

    // ════════════════════════════════════════════════════════════════
    // HEADER — white with navy bottom accent line
    // ════════════════════════════════════════════════════════════════
    const headerH = 36;
    const headerY = 12;

    // Header background — very light navy tint
    doc.setFillColor(...NAVY_LIGHT);
    doc.rect(10, headerY, pageW - 20, headerH, 'F');

    // Navy top accent bar (4mm)
    doc.setFillColor(...NAVY);
    doc.rect(10, headerY, pageW - 20, 4, 'F');

    // Logo — left side
    const logoX = margin;
    const logoY = headerY + 6;
    // Branding area bounding box (container)
    const logoBoundW = 44;
    const logoBoundH = 22;

    if (logoDataUrl) {
      try {
        // Calculate proportional dimensions using contain logic
        let drawW = logoBoundW;
        let drawH = logoBoundH;
        if (logoNaturalW > 0 && logoNaturalH > 0) {
          const scaleW = logoBoundW / logoNaturalW;
          const scaleH = logoBoundH / logoNaturalH;
          const scale = Math.min(scaleW, scaleH);
          drawW = logoNaturalW * scale;
          drawH = logoNaturalH * scale;
        }
        // Center within the bounding box
        const offsetX = (logoBoundW - drawW) / 2;
        const offsetY = (logoBoundH - drawH) / 2;
        doc.addImage(logoDataUrl, 'PNG', logoX + offsetX, logoY + offsetY, drawW, drawH, undefined, 'NONE');
      } catch (_) {
        // fallback text
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(...NAVY);
        doc.text('CX INNOVATE', logoX, logoY + 8);
        doc.text('DIGITAL', logoX, logoY + 14);
      }
    } else {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...NAVY);
      doc.text('CX INNOVATE', logoX, logoY + 8);
      doc.text('DIGITAL', logoX, logoY + 14);
    }

    // Center title block
    const titleCenterX = pageW / 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...NAVY);
    doc.text('Software License Verification', titleCenterX, headerY + 14, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...SLATE);
    doc.text('Official Verification Record', titleCenterX, headerY + 20, { align: 'center' });

    // Thin divider under subtitle
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.line(titleCenterX - 35, headerY + 22, titleCenterX + 35, headerY + 22);

    // Status badge — right side, pill style
    const badgeLabel = statusLabel;
    const badgeW = badgeLabel.length > 7 ? 30 : 24;
    const badgeX = pageW - margin - badgeW;
    const badgeY = headerY + 8;
    const badgeH = 14;
    roundedRect(badgeX, badgeY, badgeW, badgeH, 3.5, statusBg, statusBorder, 0.5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...statusColor);
    doc.text(badgeLabel, badgeX + badgeW / 2, badgeY + 8.5, { align: 'center' });

    // Bottom accent line of header
    doc.setFillColor(...NAVY_MID);
    doc.rect(10, headerY + headerH - 1, pageW - 20, 1, 'F');

    // ════════════════════════════════════════════════════════════════
    // VERIFICATION REFERENCE ROW
    // ════════════════════════════════════════════════════════════════
    let curY = headerY + headerH + 5;

    const tsFormatted = new Date(verificationData?.verificationTimestamp ?? '').toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...LABEL_CLR);
    doc.text('VERIFICATION REFERENCE', margin, curY + 3);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...NAVY_MID);
    doc.text(verificationData?.verificationReference ?? '—', margin + 44, curY + 3);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...LABEL_CLR);
    doc.text(`Verified: ${tsFormatted}`, pageW - margin, curY + 3, { align: 'right' });

    curY += 8;

    // Thin separator
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.line(margin, curY, pageW - margin, curY);
    curY += 5;

    // ════════════════════════════════════════════════════════════════
    // SECTION CARD HELPER
    // ════════════════════════════════════════════════════════════════
    const drawSectionCard = (title: string, y: number, cardH: number) => {
      // Card background
      doc.setFillColor(...WHITE);
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.25);
      doc.roundedRect(margin - 2, y, contentW + 4, cardH, 2, 2, 'FD');

      // Left navy accent bar
      doc.setFillColor(...NAVY_MID);
      doc.roundedRect(margin - 2, y, 2.5, cardH, 1, 1, 'F');
      // Square off right side of accent bar
      doc.rect(margin - 2 + 1.5, y, 1, cardH, 'F');

      // Section title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...NAVY);
      doc.text(title, margin + 4, y + 5.5);

      // Thin line under title
      doc.setDrawColor(...NAVY_LIGHT);
      doc.setLineWidth(0.2);
      doc.line(margin + 4, y + 7.5, margin + contentW - 2, y + 7.5);
    };

    // ════════════════════════════════════════════════════════════════
    // SECTION 1 — LICENSE INFORMATION
    // ════════════════════════════════════════════════════════════════
    const s1CardH = 34;
    drawSectionCard('LICENSE INFORMATION', curY, s1CardH);
    const s1ContentY = curY + 11;
    if (lic) {
      const col1X = margin + 6;
      const col2X = margin + contentW / 2 + 4;
      const colW = contentW / 2 - 10;
      labelValue('License Name', lic.license_name ?? '—', col1X, s1ContentY, colW);
      labelValue('License Number', lic.software_license_number ?? '—', col2X, s1ContentY, colW);
      labelValue('Quantity', `${lic.quantity ?? '—'} ${lic.unit_of_measure ?? ''}`.trim(), col1X, s1ContentY + 13, colW);
      labelValue('Current Status', lic.status ?? '—', col2X, s1ContentY + 13, colW, statusColor);
    }
    curY += s1CardH + 4;

    // ════════════════════════════════════════════════════════════════
    // SECTION 2 — CUSTOMER & PROJECT INFORMATION
    // ════════════════════════════════════════════════════════════════
    const s2CardH = 34;
    drawSectionCard('CUSTOMER & PROJECT INFORMATION', curY, s2CardH);
    const s2ContentY = curY + 11;
    if (lic) {
      const col1X = margin + 6;
      const col2X = margin + contentW / 2 + 4;
      const colW = contentW / 2 - 10;
      labelValue('Customer Name', lic.customer_name ?? '—', col1X, s2ContentY, colW);
      labelValue('Project Name', lic.project_name ?? '—', col2X, s2ContentY, colW);
      labelValue('Tender Reference', lic.tender_reference ?? '—', col1X, s2ContentY + 13, colW);
      labelValue('Issue Date', formatDate(lic.issue_date) ?? '—', col2X, s2ContentY + 13, colW);
    }
    curY += s2CardH + 4;

    // ════════════════════════════════════════════════════════════════
    // SECTION 3 — VALIDITY DETAILS
    // ════════════════════════════════════════════════════════════════
    const s3CardH = 24;
    drawSectionCard('VALIDITY DETAILS', curY, s3CardH);
    const s3ContentY = curY + 11;
    if (lic) {
      const col1X = margin + 6;
      const col2X = margin + contentW / 3 + 4;
      const col3X = margin + (contentW / 3) * 2 + 2;
      const colW = contentW / 3 - 6;
      labelValue('Valid From', formatDate(lic.valid_from) ?? '—', col1X, s3ContentY, colW);
      labelValue('Valid Until', formatDate(lic.valid_until) ?? '—', col2X, s3ContentY, colW);
      labelValue('Remarks', lic.remarks ?? '—', col3X, s3ContentY, colW);
    }
    curY += s3CardH + 4;

    // ════════════════════════════════════════════════════════════════
    // SECTION 4 — VERIFICATION METADATA
    // ════════════════════════════════════════════════════════════════
    const s4CardH = 24;
    drawSectionCard('VERIFICATION METADATA', curY, s4CardH);
    const s4ContentY = curY + 11;
    {
      const col1X = margin + 6;
      const col2X = margin + contentW / 2 + 4;
      const colW = contentW / 2 - 10;
      labelValue('Verification Reference', verificationData?.verificationReference ?? '—', col1X, s4ContentY, colW, NAVY_MID);
      labelValue('Verification Timestamp', tsFormatted, col2X, s4ContentY, colW);
    }
    curY += s4CardH + 4;

    // ════════════════════════════════════════════════════════════════
    // SECTION 5 — QR VERIFICATION
    // ════════════════════════════════════════════════════════════════
    if (lic) {
      const qrCardH = 46;
      drawSectionCard('QR VERIFICATION', curY, qrCardH);

      const qrSize = 32;
      const qrX = margin + 6;
      const qrY = curY + 10;

      if (qrDataUrl) {
        // Real QR code image
        doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
      } else {
        // Fallback placeholder
        roundedRect(qrX, qrY, qrSize, qrSize, 1.5, SLATE_L, BORDER, 0.3);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6);
        doc.setTextColor(...SLATE);
        doc.text('QR CODE', qrX + qrSize / 2, qrY + qrSize / 2 + 1, { align: 'center' });
      }

      // QR description text
      const textX = qrX + qrSize + 6;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...NAVY);
      doc.text('Scan to Verify Online', textX, qrY + 6);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...SLATE);
      doc.text('Use any QR scanner to access the online verification portal', textX, qrY + 12);
      doc.text('and confirm the authenticity of this license certificate.', textX, qrY + 18);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...NAVY_MID);
      const urlLines = doc.splitTextToSize(verifyUrl, contentW - qrSize - 20);
      doc.text(urlLines[0], textX, qrY + 26);

      curY += qrCardH + 4;
    }

    // ════════════════════════════════════════════════════════════════
    // FOOTER
    // ════════════════════════════════════════════════════════════════
    const footerY = pageH - 20;

    // Thin top line
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.line(margin, footerY, pageW - margin, footerY);

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6.5);
    doc.setTextColor(...SLATE);
    doc.text('This is a system-generated verification document. It is valid without signature or stamp.', pageW / 2, footerY + 5, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...LABEL_CLR);
    doc.text(
      `Generated: ${new Date().toLocaleString('en-GB')}  ·  LicenseChecker — Software License Management System`,
      pageW / 2, footerY + 10, { align: 'center' }
    );

    doc.save(`license-verification-${verificationData?.verificationReference ?? 'certificate'}.pdf`);
  };

  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/public-license-checker?license=${encodeURIComponent(inputValue.trim())}`
    : `/public-license-checker?license=${encodeURIComponent(inputValue.trim())}`;

  const cfg = verificationData ? RESULT_CONFIG[verificationData.result] : null;
  const lic = verificationData?.license;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-primary-700 text-white shadow-md print:hidden">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/assets/images/cxlogo-yJQWiUVo-1775628120741.png"
              alt="CX Innovate Digital Logo"
              className="h-8 w-auto object-contain brightness-0 invert"
            />
            <div>
              <p className="text-sm font-700 leading-tight">LicenseChecker</p>
              <p className="text-xs text-primary-300">Software License Validity Checker</p>
            </div>
          </div>
          <Link
            href="/admin-login"
            className="flex items-center gap-1.5 text-xs text-primary-200 hover:text-white transition-colors border border-primary-500 hover:border-primary-300 rounded-lg px-3 py-1.5"
          >
            <ExternalLink size={13} />
            Admin Portal
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-primary-700 to-primary-800 text-white pt-10 pb-16 print:hidden">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-xs font-500 mb-5">
            <Shield size={13} className="text-accent-500" />
            Official License Verification Portal
          </div>
          <h1 className="text-2xl sm:text-3xl font-700 mb-3 leading-tight">
            Software License Validity Checker
          </h1>
          <p className="text-sm sm:text-base text-primary-200 leading-relaxed max-w-xl mx-auto">
            Verify the authenticity and validity status of your software license by entering the Software License Number below.
          </p>
        </div>
      </section>

      {/* Search card */}
      <section className="max-w-2xl mx-auto w-full px-4 -mt-8 print:hidden">
        <div className="bg-white rounded-xl shadow-card-lg border border-slate-200 p-6">
          <label className="block text-sm font-600 text-slate-700 mb-1.5">
            Enter Software License Number
          </label>
          <p className="text-xs text-slate-400 mb-3">
            Please enter the exact Software License Number as issued. Verification is case-insensitive.
          </p>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => { setInputValue(e.target.value); setInputError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
                placeholder="e.g. XXX-MOD-TYPE-ENTITLEMENT-YYMMDD-A01"
                className={`w-full font-mono text-sm border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-700/30 focus:border-primary-700 transition-all placeholder:text-slate-300 ${inputError ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-white'}`}
              />
            </div>
            <button
              onClick={handleCheck}
              disabled={uiState === 'loading'}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-700 hover:bg-primary-800 text-white text-sm font-600 rounded-lg transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
            >
              {uiState === 'loading' ? (
                <RefreshCw size={16} className="animate-spin" />
              ) : (
                <Search size={16} />
              )}
              {uiState === 'loading' ? 'Checking...' : 'Check Validity'}
            </button>
          </div>
          {inputError && (
            <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle size={12} /> {inputError}
            </p>
          )}
          <p className="mt-3 text-xs text-slate-400">
            Please enter the exact Software License Number as issued. Verification is case-insensitive.
          </p>
        </div>
      </section>

      {/* Result */}
      {uiState === 'result' && verificationData && cfg && (
        <section ref={resultRef} className="max-w-2xl mx-auto w-full px-4 mt-6 mb-12">
          <div className={`rounded-xl border ${cfg.borderColor} overflow-hidden shadow-card-lg`}>
            {/* Result header */}
            <div className={`${cfg.headerBg} px-6 py-5 text-white`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${cfg.iconBg} flex items-center justify-center`}>
                  <cfg.icon size={20} className={cfg.iconColor} />
                </div>
                <div>
                  <p className="text-xs font-500 text-white/70 uppercase tracking-wider">Verification Result</p>
                  <p className="text-xl font-700">{cfg.title}</p>
                </div>
              </div>
              <p className="mt-3 text-sm text-white/90 leading-relaxed">{cfg.message}</p>
            </div>

            {/* Actions */}
            <div className="bg-white border-b border-slate-100 px-6 py-3 flex items-center gap-2 flex-wrap print:hidden">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 text-xs font-600 text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-all"
              >
                <Copy size={13} />
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 text-xs font-600 text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-all"
              >
                <Printer size={13} />
                Print
              </button>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-1.5 text-xs font-600 text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-all"
              >
                <Download size={13} />
                Download PDF
              </button>
              <button
                onClick={() => setShowQR(!showQR)}
                className="flex items-center gap-1.5 text-xs font-600 text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-all"
              >
                <Icon name="QrCode" size={13} />
                {showQR ? 'Hide QR' : 'Show QR'}
              </button>
              <button
                onClick={handleReset}
                className="ml-auto flex items-center gap-1.5 text-xs font-600 text-primary-700 hover:text-primary-800 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-all"
              >
                <RefreshCw size={13} />
                New Check
              </button>
            </div>

            {/* QR Code */}
            {showQR && (
              <div className="bg-slate-50 border-b border-slate-100 px-6 py-4 flex items-center gap-4 print:hidden">
                <QRCodeSVG value={shareUrl} size={80} />
                <div>
                  <p className="text-xs font-600 text-slate-700 mb-1">Shareable Verification URL</p>
                  <p className="text-xs text-slate-400 font-mono break-all">{shareUrl}</p>
                </div>
              </div>
            )}

            {/* License details */}
            {lic && (
              <div className="bg-white px-6 py-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {[
                    { label: 'Software License', value: lic.license_name },
                    { label: 'License Number', value: lic.software_license_number, mono: true },
                    { label: 'Quantity', value: `${lic.quantity} ${lic.unit_of_measure}` },
                    { label: 'Customer Name', value: lic.customer_name },
                    { label: 'Project Name', value: lic.project_name },
                    { label: 'Tender Reference', value: lic.tender_reference, mono: true },
                    { label: 'Issue Date', value: formatDate(lic.issue_date) },
                    { label: 'Valid From', value: formatDate(lic.valid_from) },
                    { label: 'Valid Until', value: formatDate(lic.valid_until) },
                    { label: 'Current Status', value: lic.status },
                    { label: 'Remarks', value: lic.remarks },
                    { label: 'Last Updated', value: formatDate(lic.updated_at) },
                  ].map((row) => (
                    <div key={`detail-${row.label}`} className="flex flex-col gap-0.5">
                      <p className="text-xs text-slate-400 font-500">{row.label}</p>
                      <p className={`text-sm font-600 text-slate-800 ${row.mono ? 'font-mono' : ''}`}>
                        {row.value || '—'}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-400 font-500">Verification Timestamp</p>
                    <p className="text-sm font-600 text-slate-700">
                      {new Date(verificationData.verificationTimestamp).toLocaleString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit', second: '2-digit',
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-500">Verification Reference</p>
                    <p className="text-sm font-700 font-mono text-primary-700">{verificationData.verificationReference}</p>
                  </div>
                </div>
              </div>
            )}

            {/* NOT_FOUND state */}
            {!lic && (
              <div className="bg-white px-6 py-8 text-center">
                <p className="text-xs text-slate-400 mb-1">Verification Reference</p>
                <p className="text-sm font-700 font-mono text-slate-600">{verificationData.verificationReference}</p>
                <p className="text-xs text-slate-400 mt-3">
                  {new Date(verificationData.verificationTimestamp).toLocaleString('en-GB')}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="mt-auto bg-white border-t border-slate-200 py-4 print:hidden">
        <div className="max-w-screen-xl mx-auto px-4 text-center">
          <p className="text-xs text-slate-400">
            © 2026 Software License Management & Validity Checker. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
